import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../../utils/formatCurrency';
import ElapsedTimer from '../ElapsedTimer';
import './TableOrderCard.css';

const PAYMENT_CONFIG = {
  cash: { emoji: '💵', labelKey: 'orders.markPaidCash', defaultLabel: 'Cobrar efectivo' },
  card: { emoji: '💳', labelKey: 'orders.markPaidCard', defaultLabel: 'Cobrar tarjeta' },
  transfer: { emoji: '🏦', labelKey: 'orders.markPaidTransfer', defaultLabel: 'Cobrar transferencia' },
  mercadopago: { emoji: '📱', labelKey: 'orders.markPaidMercadoPago', defaultLabel: 'Cobrar Mercado Pago' },
};

const STATUS_ICONS = {
  pending: '🟠',
  preparing: '🔵',
  ready: '🟢',
  delivered: '✅',
  cancelled: '❌',
};

const TableOrderCard = React.memo(function TableOrderCard({ order, onStatusChange, onMarkPaid, onEditOrder, onCancelOrder, paymentMethods }) {
  const { t } = useTranslation();

  const statusIcon = STATUS_ICONS[order.status] || '⬜';
  const isPending = order.status === 'pending';
  const isReady = order.status === 'ready';
  const isPaid = order.paymentStatus === 'paid';
  const items = order.items || [];

  // Derive enabled payment methods from restaurant config
  const enabledMethods = paymentMethods
    ? Object.entries(paymentMethods)
        .filter(([, config]) => config.enabled)
        .map(([key]) => key)
        .filter((key) => PAYMENT_CONFIG[key])
    : ['cash', 'card'];

  return (
    <div className={`table-order-card table-order-card--${order.status}`}>
      <div className="table-order-header">
        <div className="table-order-id">
          {t('orders.orderNumber')}{order.orderId?.slice(-6)}
          {order.coordinated && (
            <span className="table-order-coordinated-badge">
              <span className="material-icons" aria-hidden="true" style={{ fontSize: '12px' }}>link</span>
              {t('coordination.coordinated')}
            </span>
          )}
        </div>
        <div className="table-order-status">
          {statusIcon} {t(`orders.status.${order.status}`)}
        </div>
      </div>
      {order.coordinated && (order.kitchenStatus || order.barStatus) && (
        <div className={`table-order-coordination-status ${order.kitchenStatus === 'ready' && order.barStatus === 'ready' ? 'coordination-ready' : 'coordination-waiting'}`}>
          {order.kitchenStatus === 'ready' && order.barStatus === 'ready'
            ? t('coordination.readyToServe')
            : order.kitchenStatus !== 'ready'
              ? t('coordination.waitingKitchen')
              : t('coordination.waitingBar')}
        </div>
      )}

      <div className="table-order-items">
        {items.map((item, idx) => (
          <div key={idx} className="table-order-item">
            <span className="table-order-item-qty">{item.quantity}x</span>
            <span className="table-order-item-name">{item.name}</span>
            {item.modifiers?.length > 0 && (
              <span className="table-order-item-mods">
                ({item.modifiers.map((m) => m.name || m).join(', ')})
              </span>
            )}
          </div>
        ))}
      </div>

      <div className="table-order-footer">
        <div className="table-order-meta">
          <ElapsedTimer
            createdAt={order.createdAt}
            render={(elapsedMinutes) => (
              <span className="table-order-time">{t('orders.minutes', { count: elapsedMinutes })}</span>
            )}
          />
          <span className="table-order-total">
            {formatCurrency(order.total)}
          </span>
          {isPaid && <span className="table-order-paid-badge">{t('orders.paid')}</span>}
        </div>

        <div className="table-order-actions">
          {isPending && onEditOrder && (
            <button
              className="table-order-action-btn action-edit"
              onClick={() => onEditOrder(order)}
              title={t('orders.editOrder')}
              aria-label={t('orders.editOrder')}
            >
              <span className="material-icons" aria-hidden="true" style={{ fontSize: '16px' }}>edit</span>
            </button>
          )}
          {isPending && onCancelOrder && (
            <button
              className="table-order-action-btn action-cancel"
              onClick={() => onCancelOrder(order)}
              title={t('orders.cancelOrder')}
              aria-label={t('orders.cancelOrder')}
            >
              <span className="material-icons" aria-hidden="true" style={{ fontSize: '16px' }}>delete</span>
            </button>
          )}
          {isPending && (
            <button
              className="table-order-action-btn action-prepare"
              onClick={() => onStatusChange(order.orderId, 'preparing')}
            >
              {t('waiter.sendToPrepare')}
            </button>
          )}
          {isReady && (
            <button
              className="table-order-action-btn action-deliver"
              onClick={() => onStatusChange(order.orderId, 'delivered')}
            >
              {t('waiter.markDelivered')}
            </button>
          )}
          {!isPaid && order.status !== 'cancelled' && (
            <>
              {enabledMethods.map((method) => {
                const config = PAYMENT_CONFIG[method];
                const label = t(config.labelKey, config.defaultLabel);
                const shortId = order.orderId?.slice(-6);
                const ariaLabel = t('orders.markPaidAriaLabel', '{{method}} pedido #{{orderId}}', { method: label, orderId: shortId });
                return (
                  <button
                    key={method}
                    className={`table-order-action-btn action-${method}`}
                    onClick={() => onMarkPaid(order, method)}
                    title={label}
                    aria-label={ariaLabel}
                  >
                    {config.emoji}
                  </button>
                );
              })}
            </>
          )}
        </div>
      </div>
    </div>
  );
});

export default TableOrderCard;
